export const SITE_URL = 'https://udesignstudio.com.au';
export const ORGANIZATION_ID = `${SITE_URL}/#organization`;
export const LOCAL_BUSINESS_ID = `${SITE_URL}/#building-design-studio`;

export const PROTECTED_TERMS = [
  /\barchitects?\b/i,
  /\barchitecture\b/i,
  /\barchitectural\b/i,
];

export type SeoRoute = {
  path: string;
  title: string;
  description: string;
  h1: string;
  canonical: string;
  kind: 'page' | 'service' | 'project';
  image?: string;
};

type RouteInput = Omit<SeoRoute, 'canonical'>;

const route = (input: RouteInput): SeoRoute => ({
  ...input,
  canonical: input.path === '/' ? `${SITE_URL}/` : `${SITE_URL}${input.path}`,
});

export const SEO_ROUTES: SeoRoute[] = [
  route({
    path: '/',
    title: 'Building Design Studio Melbourne | U Design',
    description: 'Melbourne building design studio for townhouses, multi-unit developments, custom homes, commercial spaces, interiors and project coordination.',
    h1: 'Building Design Studio Melbourne',
    kind: 'page',
    image: '/images/home-hero-1600.webp',
  }),
  route({
    path: '/services',
    title: 'Building Design Services Melbourne | U Design',
    description: 'Explore Melbourne building design, interiors, planning coordination and project management services for residential and commercial projects.',
    h1: 'Building Design Services',
    kind: 'page',
    image: '/images/service-building-design-studio-optimized.webp',
  }),
  route({
    path: '/portfolio',
    title: 'Melbourne Building Design Portfolio | U Design',
    description: 'View completed and approved townhouse, custom home, hospitality, retail and digital projects delivered by U Design across greater Melbourne.',
    h1: 'Melbourne Building Design Portfolio',
    kind: 'page',
    image: '/images/u018068_tp_03_.webp',
  }),
  route({
    path: '/about',
    title: 'About U Design | Melbourne Building Design Studio',
    description: 'Meet U Design, a Melbourne building design studio with 15+ years coordinating residential, commercial, interiors and project delivery work.',
    h1: 'About U Design',
    kind: 'page',
    image: '/images/udesign-mark-112.webp',
  }),
  route({
    path: '/contact',
    title: 'Contact U Design | Building Designer Melbourne',
    description: 'Discuss your Melbourne townhouse, custom home, commercial or interior project with U Design. Enquire online or visit our Kew design studio.',
    h1: 'Start Your Project',
    kind: 'page',
    image: '/images/udesign-mark-112.webp',
  }),
  route({
    path: '/residential-development-design-melbourne',
    title: 'Townhouse & Multi-Unit Design Melbourne | U Design',
    description: 'Townhouse and multi-unit building design in Melbourne, from site feasibility and concept design to planning documents and detailed drawings.',
    h1: 'Townhouse & Multi-Unit Design Melbourne',
    kind: 'service',
    image: '/images/u018068_tp_03_.webp',
  }),
  route({
    path: '/custom-home-design-melbourne',
    title: 'Custom Home Design Melbourne | U Design Studio',
    description: 'Custom and luxury home design across Melbourne, combining site-responsive planning, refined interiors, 3D visualisation and detailed drawings.',
    h1: 'Custom Home Design Melbourne',
    kind: 'service',
    image: '/images/toorak_luxury_04_20swim.webp',
  }),
  route({
    path: '/commercial-building-design-melbourne',
    title: 'Commercial Building Design Melbourne | U Design',
    description: 'Commercial building design for Melbourne retail, hospitality and mixed-use spaces, including fit-outs, planning documentation and interiors.',
    h1: 'Commercial Building Design Melbourne',
    kind: 'service',
    image: '/images/u018067_cafe_01_.webp',
  }),
  route({
    path: '/interior-design-melbourne',
    title: 'Interior Design Melbourne | U Design Studio',
    description: 'Residential, retail and hospitality interior design in Melbourne, with space planning, material selection, joinery, lighting and documentation.',
    h1: 'Interior Design Melbourne',
    kind: 'service',
    image: '/images/service-interior-design.webp',
  }),
  route({
    path: '/planning-project-management-melbourne',
    title: 'Planning & Project Management Melbourne | U Design',
    description: 'Melbourne planning coordination and project management for residential and commercial work, from consultant briefing to handover support.',
    h1: 'Planning & Project Management Melbourne',
    kind: 'service',
    image: '/images/service-project-management-optimized.webp',
  }),
  route({
    path: '/portfolio/mitcham-townhouse-subdivision',
    title: 'Mitcham Townhouse Subdivision | U Design',
    description: 'See how U Design coordinated a five-townhouse Mitcham project from pre-purchase review and working drawings through construction and settlement.',
    h1: 'Mitcham Townhouse Subdivision',
    kind: 'project',
    image: '/images/u018068_tp_03_.webp',
  }),
  route({
    path: '/portfolio/ringwood-terrace-townhouses',
    title: 'Ringwood Terrace Townhouses | U Design',
    description: 'Explore a VCAT-approved five-lot terrace townhouse scheme in Ringwood, developed through detailed planning research and clear 3D design studies.',
    h1: 'Ringwood Terrace Townhouses',
    kind: 'project',
    image: '/images/a018058_rev2_01_pitt_street_.webp',
  }),
  route({
    path: '/portfolio/toorak-luxury-residence',
    title: 'Toorak Luxury Residence | U Design Studio',
    description: 'Tour a custom two-storey Toorak residence with a 20-metre pool, landscaped gardens, refined interiors and seamless indoor-outdoor living.',
    h1: 'Toorak Luxury Residence',
    kind: 'project',
    image: '/images/toorak_luxury_04_20swim.webp',
  }),
  route({
    path: '/portfolio/carlton-cafe-patisserie',
    title: 'Carlton Cafe & Patisserie Fit-Out | U Design',
    description: 'See a compact Carlton cafe and patisserie fit-out designed for efficient staff workflow, generous display space and comfortable customer seating.',
    h1: 'Carlton Cafe & Patisserie',
    kind: 'project',
    image: '/images/u018067_cafe_01_.webp',
  }),
  route({
    path: '/portfolio/yering-hotel',
    title: 'Yering Hotel & Function Facilities | U Design',
    description: 'Explore U Design hospitality work in Yering, including hotel renovation, guest facilities, event spaces and a rooftop function venue concept.',
    h1: 'Yering Hotel & Function Facilities',
    kind: 'project',
    image: '/images/yering_gathering_hall_01_front_dusk.webp',
  }),
];

export function getSeoRoute(pathname: string): SeoRoute {
  return SEO_ROUTES.find((item) => item.path === pathname) ?? SEO_ROUTES[0];
}

export function routeOutputFile(pathname: string): string {
  return pathname === '/' ? 'index.html' : `${pathname.slice(1)}.html`;
}

export function buildPageStructuredData(page: SeoRoute) {
  const graph: Record<string, unknown>[] = [
    {
      '@type': 'Organization',
      '@id': ORGANIZATION_ID,
      name: 'U Design Studio',
      url: `${SITE_URL}/`,
      logo: `${SITE_URL}/images/udesign-mark-112.webp`,
      image: `${SITE_URL}/images/og-image.jpg`,
      email: 'admin@udesignstudio.com.au',
      telephone: '+61 3 9008 6321',
      sameAs: [],
    },
    {
      '@type': 'ProfessionalService',
      '@id': LOCAL_BUSINESS_ID,
      name: 'U Design Studio',
      description: 'Melbourne building design studio for residential developments, custom homes, commercial spaces and interiors.',
      url: `${SITE_URL}/`,
      image: `${SITE_URL}/images/og-image.jpg`,
      logo: `${SITE_URL}/images/udesign-mark-112.webp`,
      email: 'admin@udesignstudio.com.au',
      telephone: '+61 3 9008 6321',
      priceRange: '$$$',
      parentOrganization: { '@id': ORGANIZATION_ID },
      address: {
        '@type': 'PostalAddress',
        streetAddress: 'Suite 218/89 High Street',
        addressLocality: 'Kew',
        addressRegion: 'VIC',
        postalCode: '3101',
        addressCountry: 'AU',
      },
      geo: {
        '@type': 'GeoCoordinates',
        latitude: -37.8074,
        longitude: 145.033,
      },
      openingHoursSpecification: [{
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
        opens: '09:00',
        closes: '17:00',
      }],
      areaServed: { '@type': 'City', name: 'Melbourne' },
      knowsAbout: [
        'Building Design',
        'Townhouse Design',
        'Multi-Unit Residential Design',
        'Custom Home Design',
        'Interior Design',
        'Town Planning Documentation',
        'Project Management',
      ],
    },
    {
      '@type': 'WebPage',
      '@id': `${page.canonical}#webpage`,
      url: page.canonical,
      name: page.title,
      description: page.description,
      isPartOf: { '@id': `${SITE_URL}/#website` },
      about: { '@id': LOCAL_BUSINESS_ID },
      primaryImageOfPage: page.image ? `${SITE_URL}${page.image}` : undefined,
    },
    {
      '@type': 'BreadcrumbList',
      '@id': `${page.canonical}#breadcrumb`,
      itemListElement: page.path === '/' ? [{
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: `${SITE_URL}/`,
      }] : [
        { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
        { '@type': 'ListItem', position: 2, name: page.h1, item: page.canonical },
      ],
    },
  ];

  if (page.kind === 'service') {
    graph.push({
      '@type': 'Service',
      '@id': `${page.canonical}#service`,
      name: page.h1,
      description: page.description,
      provider: { '@id': LOCAL_BUSINESS_ID },
      areaServed: { '@type': 'City', name: 'Melbourne' },
      url: page.canonical,
    });
  }

  if (page.kind === 'project') {
    graph.push({
      '@type': 'CreativeWork',
      '@id': `${page.canonical}#project`,
      name: page.h1,
      description: page.description,
      creator: { '@id': ORGANIZATION_ID },
      image: page.image ? `${SITE_URL}${page.image}` : undefined,
      url: page.canonical,
    });
  }

  return { '@context': 'https://schema.org', '@graph': graph };
}
